import React, { useCallback, useRef, useState } from 'react';
import clsx from 'clsx';
import { Alert, Button, Dropdown, Menu } from 'antd';
import { differenceInDays, format } from 'date-fns';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import { AppNotification } from 'types';
import { useAppDispatch, useAppSelector } from 'shared/store';
import {
  fetchAppNotifications,
  readAppNotification,
  removeAppNotification,
  selectAppNotificationsList,
  isAppNotificationPopupFresh,
} from 'shared/store/app-notification-slice';
import Loader from 'shared/components/Loader';
import { Icon } from 'shared/components/Icon';
import { BellMessage } from 'shared/components/BellMessage';
import { AppNotificationItem } from '../AppNotificationItem';
import './style.less';

const NAME = 'AppNotificationList';
export const AppNotificationList: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    hasLoaded,
    isLoading,
    getListError,
    hasMore,
    earliestDateCreated,
  } = useAppSelector(s => s.appNotification);
  const list = useAppSelector(selectAppNotificationsList);

  const onLoadMore = useCallback(async () => {
    if (isLoading || !earliestDateCreated) {
      return;
    }
    await dispatch(fetchAppNotifications());
  }, [dispatch, isLoading, earliestDateCreated]);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const [sentryRef] = useInfiniteScroll({
    loading: isLoading,
    hasNextPage: hasMore && !getListError,
    onLoadMore,
  });

  return (
    <div className={NAME} ref={containerRef}>
      {getListError && (
        <Alert
          className={`${NAME}__Alert`}
          type="warning"
          message="Could not load app notifications"
          description={getListError}
        />
      )}
      {list.map((n, i) => {
        return (
          <React.Fragment key={n.id}>
            <TimeSeparator prev={i ? list[i - 1] : null} current={n} />
            <ListItem an={n} />
          </React.Fragment>
        );
      })}

      {hasLoaded && !list.length && (
        <BellMessage
          className={`${NAME}__empty`}
          type="empty"
          title="Looks like you're all caught up!"
          text="No new notifications"
        />
      )}

      {hasLoaded && (isLoading || hasMore) && (
        <div ref={sentryRef} className={`${NAME}__item-loader`}>
          <Loader inline size="small" />
        </div>
      )}
    </div>
  );
};

const DEL_ANIM_DUR = 300;
const ListItem: React.FC<{ an: AppNotification }> = ({ an }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [delPhase, setDelPhase] = useState(0);
  const dispatch = useAppDispatch();

  const handleDelete = () => {
    // phase 1 lock height
    setDelPhase(1);
    // phase 2 set transition on height and set height to 0
    setTimeout(() => setDelPhase(2), 1);
    // call delete api
    setTimeout(() => {
      dispatch(removeAppNotification(an.id));
    }, DEL_ANIM_DUR);
  };

  const handleRead = () => {
    dispatch(readAppNotification(an.id));
  };

  const style =
    delPhase === 1 && ref.current
      ? { height: `${ref.current.getBoundingClientRect().height}px` }
      : delPhase === 2 && ref.current
      ? { height: '0px', transition: `height ${DEL_ANIM_DUR}ms` }
      : {};

  return (
    <React.Fragment>
      <div ref={ref} style={style} className={clsx(`${NAME}__wrapper`)}>
        <div className={`${NAME}__hover-overlay`}>
          <Dropdown
            overlay={
              <Menu className={`${NAME}__Menu`}>
                <Menu.Item onClick={handleRead} disabled={!!an.dateRead}>
                  Mark as read
                </Menu.Item>
                <Menu.Item onClick={handleDelete} style={{ color: 'red' }}>
                  Delete
                </Menu.Item>
              </Menu>
            }
          >
            <Button>
              Options <Icon icon="chevronOn" />
            </Button>
          </Dropdown>
        </div>
        <AppNotificationItem notification={an} />
      </div>
    </React.Fragment>
  );
};

const makeDateSepHash = (d: Date) => `${d.getFullYear()}:${d.getMonth()}:${d.getDate()}`;
const TimeSeparator: React.FC<{
  current: AppNotification;
  prev: AppNotification | null;
}> = ({ current, prev }) => {
  let timeGrouping = null;
  const ts = new Date(current.dateCreated);
  const curFresh = isAppNotificationPopupFresh(current);
  const dateSepHash = makeDateSepHash(ts);

  // First item
  if (!prev) {
    if (curFresh) {
      timeGrouping = <>Now</>;
    } else {
      const daysDiff = differenceInDays(new Date(), ts);
      timeGrouping = (
        <>
          {daysDiff === 0
            ? 'Today'
            : daysDiff === 1
            ? 'Yesterday'
            : format(ts, 'eee, MMM d')}
        </>
      );
    }
  }
  // Subsequent item
  else {
    const prevTs = new Date(prev.dateCreated);
    const prevFresh = isAppNotificationPopupFresh(prev);
    const prevDateSepHash = makeDateSepHash(prevTs);
    if (prevFresh && !curFresh) {
      timeGrouping = <>Today</>;
    } else if (prevDateSepHash !== dateSepHash) {
      const now = new Date();
      const daysDiff = differenceInDays(now, ts);
      timeGrouping = <>{daysDiff === 0 ? 'Yesterday' : format(ts, 'eee, MMM d')}</>;
    }
  }

  return <div className={`${NAME}__TimeSeparator`}>{timeGrouping}</div>;
};
